import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Box } from '../../../components/Box.tsx'
import { Alert } from '../../../components/generic.tsx'
import { I18n } from '../../../components/I18n.tsx'
import { Icon } from '../../../components/Icon.tsx'
import type { AssetLocation } from '../../../data/locations.tsx'
import { ids } from '../../../ids.ts'
import { useMap } from '../../../states.ts'
import { useMapCursorPoint } from '../../map/mapUtils.ts'
import {
	isRectangle,
	locationsToAdd,
	type MapSelectionMode,
	type PixelPoint,
	type PixelRectangle,
	rectangleBetween,
} from './mapSelection.ts'

type Drag = { start: PixelPoint; current: PixelPoint }

export const MapSelectionOverlay = ({
	mode,
	locations,
	selectedLocations,
	maxAssets,
	onAdd,
	onDragSelected,
}: {
	mode: MapSelectionMode
	locations: AssetLocation[]
	selectedLocations: AssetLocation[]
	maxAssets: number
	onAdd: (locations: AssetLocation[]) => void
	onDragSelected: () => void
}) => {
	const map = useMap((s) => s.map)
	const [pointer, setPointer] = useState<PixelPoint>()
	const onPointChange = useCallback((point: [number, number] | undefined) => setPointer(point && { x: point[0], y: point[1] }), [])
	useMapCursorPoint(onPointChange)

	const toAdd = (rectangle: PixelRectangle) =>
		locationsToAdd(
			locations,
			selectedLocations,
			rectangle,
			({ lon, lat }) => {
				const { x, y } = map?.project([lon, lat]) ?? { x: Number.NaN, y: Number.NaN }
				return { x, y }
			},
			maxAssets,
		)

	const drag = useDrag(map, mode, (rectangle) => {
		if (!isRectangle(rectangle)) return
		const added = toAdd(rectangle)
		if (added.length > 0) onAdd(added)
		onDragSelected()
	})

	const container = map?.getContainer()
	const bounds = { width: container?.clientWidth ?? 0, height: container?.clientHeight ?? 0 }
	const rectangle = drag && rectangleBetween(drag.start, drag.current)

	if (rectangle && isRectangle(rectangle)) {
		return (
			<>
				<DragRectangle rectangle={rectangle} />
				<Hint x={rectangle.left} y={rectangle.top - 50} bounds={bounds}>
					<I18n i18nKey={($) => $.reports.form.mapSelection.addAssets} count={toAdd(rectangle).length} />
				</Hint>
			</>
		)
	}

	if (drag || !pointer) return null

	return (
		<Hint x={pointer.x + 13} y={pointer.y - 46} bounds={bounds}>
			{mode === 'click' ? (
				<I18n i18nKey={($) => $.reports.form.mapSelection.clickHint} />
			) : (
				<I18n i18nKey={($) => $.reports.form.mapSelection.dragHint} />
			)}
		</Hint>
	)
}

const pointInContainer =
	(container: HTMLElement) =>
	(event: MouseEvent): PixelPoint => {
		const { left, top } = container.getBoundingClientRect()
		return { x: event.clientX - left, y: event.clientY - top }
	}

const startsOnMap = (event: PointerEvent, canvas: HTMLCanvasElement) =>
	event.target === canvas || (event.target instanceof Element && event.target.closest('.mapboxgl-marker') !== null)

// Follows a drag from the pointer going down to it going up, or to Escape, which cancels it
const trackDrag = (
	start: PixelPoint,
	pointOf: (event: MouseEvent) => PixelPoint,
	onChange: (drag: Drag | undefined) => void,
	onEnd: (rectangle: PixelRectangle) => void,
) => {
	const stop = () => {
		window.removeEventListener('pointermove', onPointerMove)
		window.removeEventListener('pointerup', onPointerUp)
		window.removeEventListener('keydown', onKeyDown)
		document.body.style.userSelect = ''
		onChange(undefined)
	}
	const onPointerMove = (move: PointerEvent) => onChange({ start, current: pointOf(move) })
	const onPointerUp = (up: PointerEvent) => {
		stop()
		onEnd(rectangleBetween(start, pointOf(up)))
	}
	const onKeyDown = (key: KeyboardEvent) => {
		if (key.key === 'Escape') stop()
	}

	onChange({ start, current: start })
	document.body.style.userSelect = 'none'
	window.addEventListener('pointermove', onPointerMove)
	window.addEventListener('pointerup', onPointerUp)
	window.addEventListener('keydown', onKeyDown)
}

const useDrag = (map: mapboxgl.Map | undefined, mode: MapSelectionMode, onEnd: (rectangle: PixelRectangle) => void) => {
	const [drag, setDrag] = useState<Drag>()
	const latestOnEnd = useRef(onEnd)
	latestOnEnd.current = onEnd

	useEffect(() => {
		if (!map || mode !== 'drag') return
		const container = map.getContainer()
		const canvas = map.getCanvas()
		const pointOf = pointInContainer(container)
		const onPointerDown = (event: PointerEvent) => {
			if (event.button !== 0 || !startsOnMap(event, canvas)) return
			trackDrag(pointOf(event), pointOf, setDrag, (rectangle) => latestOnEnd.current(rectangle))
		}
		container.addEventListener('pointerdown', onPointerDown)
		return () => container.removeEventListener('pointerdown', onPointerDown)
	}, [map, mode])

	return drag
}

const DragRectangle = ({ rectangle }: { rectangle: PixelRectangle }) => (
	<Box
		data-ta={ids.reports.form.mapSelection.dragRectangle}
		sx={{
			position: 'absolute',
			left: rectangle.left,
			top: rectangle.top,
			width: rectangle.right - rectangle.left,
			height: rectangle.bottom - rectangle.top,
			border: '1.5px dashed',
			borderColor: 'info.main',
			pointerEvents: 'none',
			zIndex: 1,
		}}
	>
		<Box sx={{ position: 'absolute', inset: 0, bgcolor: 'info.main', opacity: 0.15 }} />
	</Box>
)

// Kept inside the map, so a hint near an edge sits beside the cursor instead of running off the map
const Hint = ({ x, y, bounds, children }: { x: number; y: number; bounds: { width: number; height: number }; children: ReactNode }) => {
	const ref = useRef<HTMLDivElement>(null)
	const [size, setSize] = useState({ width: 0, height: 0 })
	useLayoutEffect(() => {
		const width = ref.current?.offsetWidth ?? 0
		const height = ref.current?.offsetHeight ?? 0
		setSize((previous) => (previous.width === width && previous.height === height ? previous : { width, height }))
	})

	return (
		<Alert
			ref={ref}
			data-ta={ids.reports.form.mapSelection.hint}
			icon={<Icon name='CircleInfoSolid' />}
			severity='info'
			sx={{
				position: 'absolute',
				left: Math.max(8, Math.min(x, bounds.width - size.width - 8)),
				top: Math.max(8, Math.min(y, bounds.height - size.height - 8)),
				py: 0,
				whiteSpace: 'nowrap',
				pointerEvents: 'none',
				zIndex: 2,
			}}
		>
			{children}
		</Alert>
	)
}
